import {
  DashboardResponse,
  DashboardStructure,
  LegacyDashboardStructure,
  MarketApps,
  NestedDashboardStructure
} from '@/features/Dashboard/types';

/**
 * Type guard to check if the structure is legacy format
 */
export const isLegacyStructure = (
  structure: DashboardResponse
): structure is LegacyDashboardStructure => {
  return 'content' in structure && Array.isArray(structure.content);
};

/**
 * Type guard to check if the structure is new nested format
 */
export const isNestedStructure = (
  structure: DashboardResponse
): structure is NestedDashboardStructure => {
  return !isLegacyStructure(structure) && typeof structure === 'object' && structure !== null;
};

/**
 * Convert legacy structure to nested structure
 * Creates a default dashboard with a default tab
 */
export const convertLegacyToNested = (
  legacyStructure: LegacyDashboardStructure,
  defaultDashboardName = 'My Dashboard',
  defaultTabName = 'Main'
): NestedDashboardStructure => {
  return {
    [defaultDashboardName]: {
      [defaultTabName]: legacyStructure.content
    }
  };
};

/**
 * Convert nested structure to legacy structure
 * Takes the first dashboard's first tab as the content
 */
export const convertNestedToLegacy = (
  nestedStructure: NestedDashboardStructure
): LegacyDashboardStructure => {
  const dashboardNames = Object.keys(nestedStructure);
  if (dashboardNames.length === 0) {
    return { content: [] };
  }

  const firstDashboard = nestedStructure[dashboardNames[0]];
  const tabNames = Object.keys(firstDashboard);
  if (tabNames.length === 0) {
    return { content: [] };
  }

  const firstTab = firstDashboard[tabNames[0]];
  return { content: firstTab };
};

/**
 * Extract MarketApps from any dashboard structure
 * For legacy: returns content directly
 * For nested: returns first dashboard's first tab
 */
export const extractMarketApps = (structure: DashboardResponse): MarketApps => {
  if (isLegacyStructure(structure)) {
    return structure.content;
  }

  if (isNestedStructure(structure)) {
    const converted = convertNestedToLegacy(structure);
    return converted.content;
  }

  return [];
};

/**
 * Get all dashboard names from nested structure
 */
export const getDashboardNames = (structure: DashboardResponse): string[] => {
  if (isLegacyStructure(structure)) {
    return ['My Dashboard']; // Default name for legacy
  }

  if (isNestedStructure(structure)) {
    return Object.keys(structure);
  }

  return [];
};

/**
 * Get all tab names for a specific dashboard
 */
export const getTabNames = (structure: DashboardResponse, dashboardName: string): string[] => {
  if (isLegacyStructure(structure)) {
    return ['Main']; // Default tab name for legacy
  }

  if (isNestedStructure(structure) && structure[dashboardName]) {
    return Object.keys(structure[dashboardName]);
  }

  return [];
};

/**
 * Get MarketApps for a specific dashboard and tab
 */
export const getMarketAppsForTab = (
  structure: DashboardResponse,
  dashboardName: string,
  tabName: string
): MarketApps => {
  if (isLegacyStructure(structure)) {
    return structure.content;
  }

  if (isNestedStructure(structure) && structure[dashboardName]?.[tabName]) {
    return structure[dashboardName][tabName];
  }

  return [];
};

/**
 * Create a new nested structure with updated MarketApps for a specific tab
 */
export const updateMarketAppsInStructure = (
  structure: DashboardResponse,
  dashboardName: string,
  tabName: string,
  marketApps: MarketApps
): DashboardStructure => {
  if (isLegacyStructure(structure)) {
    // Convert legacy to nested and update
    const nested = convertLegacyToNested(structure, dashboardName, tabName);
    return {
      ...nested,
      [dashboardName]: {
        ...nested[dashboardName],
        [tabName]: marketApps
      }
    };
  }

  if (isNestedStructure(structure)) {
    return {
      ...structure,
      [dashboardName]: {
        ...structure[dashboardName],
        [tabName]: marketApps
      }
    };
  }

  // Fallback: create new nested structure
  return {
    [dashboardName]: {
      [tabName]: marketApps
    }
  };
};

/**
 * Normalize structure for internal use - always return nested format
 */
export const normalizeStructure = (
  structure: DashboardResponse,
  defaultDashboardName = 'My Dashboard',
  defaultTabName = 'Main'
): NestedDashboardStructure => {
  if (isLegacyStructure(structure)) {
    return convertLegacyToNested(structure, defaultDashboardName, defaultTabName);
  }

  if (isNestedStructure(structure)) {
    return structure;
  }

  // Fallback for empty or invalid structure
  return {
    [defaultDashboardName]: {
      [defaultTabName]: []
    }
  };
};
